import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { MemberPlanDto } from '@pokerroom/types';
import { api } from '../../../lib/api';
import { safeFormatDate } from '../../../lib/format';
import { errorMessage } from './errorMessage';
import {
  BUTTON,
  Field,
  FormError,
  INPUT,
  PANEL,
  SMALL_BUTTON,
  TournamentSelect,
  useWalletPermissions,
} from './walletShared';

/** YYYY-MM of this month and the next, Asia/Taipei. */
function taipeiMonths(now = new Date()) {
  const shifted = new Date(now.getTime() + 8 * 3600_000);
  const year = shifted.getUTCFullYear();
  const month = shifted.getUTCMonth();
  const fmt = (y: number, m: number) =>
    `${y + Math.floor(m / 12)}-${String((m % 12) + 1).padStart(2, '0')}`;
  return [fmt(year, month), fmt(year, month + 1)] as const;
}

/** 月友: this month's and next month's plan, valid at every venue (Q27). */
export function MonthlyPlanPanel({
  memberId,
  locked,
  integratedSales = false,
}: {
  memberId: string;
  locked: boolean;
  integratedSales?: boolean;
}) {
  const { t } = useTranslation('member');
  const qc = useQueryClient();
  const { canAdjust, canManage } = useWalletPermissions();
  const [current, next] = taipeiMonths();
  const [month, setMonth] = useState<string>(current);
  const [payment, setPayment] = useState<'CASH' | 'ZIPA'>('CASH');
  const [note, setNote] = useState('');
  const [tournamentId, setTournamentId] = useState('');
  const [useNote, setUseNote] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  const { data: plans } = useQuery<MemberPlanDto[]>({
    queryKey: ['member-plans', memberId],
    queryFn: () => api.get(`/members/${memberId}/plans`),
  });
  const refresh = () => {
    setError('');
    void qc.invalidateQueries({ queryKey: ['member-plans', memberId] });
    void qc.invalidateQueries({ queryKey: ['member-ledger-summary', memberId] });
    void qc.invalidateQueries({ queryKey: ['member-zipa', memberId] });
  };
  const onError = (failure: unknown) => setError(errorMessage(failure, t('ledgerLoadFailed')));
  const sell = useMutation({
    mutationFn: () => api.post(`/members/${memberId}/plans`, { month, payment, note: note.trim() }),
    onSuccess: () => {
      setNote('');
      refresh();
    },
    onError,
  });
  const record = useMutation({
    mutationFn: (planId: string) =>
      api.post(`/members/${memberId}/plans/${planId}/uses`, {
        tournamentId,
        note: useNote.trim(),
      }),
    onSuccess: () => {
      setUseNote('');
      setTournamentId('');
      refresh();
    },
    onError,
  });
  const reverse = useMutation({
    mutationFn: (input: { planId: string; useId: string }) =>
      api.post(`/members/${memberId}/plans/${input.planId}/uses/${input.useId}/reverse`, {
        reason: reason.trim(),
      }),
    onSuccess: () => {
      setReason('');
      refresh();
    },
    onError,
  });
  const voidPlan = useMutation({
    mutationFn: (planId: string) =>
      api.post(`/members/${memberId}/plans/${planId}/void`, { reason: reason.trim() }),
    onSuccess: () => {
      setReason('');
      refresh();
    },
    onError,
  });

  const shown = (plans ?? []).filter((p) => p.month === current || p.month === next);
  const active = shown.find((p) => p.month === current && !p.voidedAt);

  return (
    <section className={PANEL} aria-labelledby="monthly-plan-title">
      <h3 id="monthly-plan-title" className="font-medium">
        {t('walletPlanTitle')}
      </h3>
      <p className="text-xs text-base-content/60">{t('walletPlanHint')}</p>

      {shown.length === 0 && <p className="text-sm text-base-content/50">{t('walletPlanNone')}</p>}
      {shown.map((plan) => (
        <div key={plan.id} className="rounded-md border border-base-300 bg-base-100 p-3 space-y-2">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="font-medium">{plan.month}</span>
            <span
              className={`text-xs px-2 py-0.5 rounded-full ${plan.voidedAt ? 'bg-base-300 text-base-content/60' : 'bg-success/15 text-success'}`}
            >
              {plan.voidedAt ? t('walletStatus_VOID') : t('walletPlanPaid')}
            </span>
            <span className="text-base-content/60">
              NT${Number(plan.price).toLocaleString()} · {t(`walletPayment_${plan.payment}`)} ·{' '}
              {plan.soldVenueName ?? '-'}
            </span>
            {canManage && !plan.voidedAt && !locked && (
              <button
                type="button"
                className={SMALL_BUTTON}
                disabled={!reason.trim() || voidPlan.isPending}
                onClick={() => voidPlan.mutate(plan.id)}
              >
                {t('walletVoid')}
              </button>
            )}
          </div>
          {plan.uses.length > 0 && (
            <ul className="text-sm divide-y divide-base-300">
              {plan.uses.map((use) => (
                <li key={use.id} className="flex flex-wrap items-center gap-2 py-1">
                  <span>{t('walletPlanUse')}</span>
                  <span className="text-base-content/60">
                    {use.venueName ?? '-'} ·{' '}
                    {safeFormatDate(use.createdAt, (d) => d.toLocaleString())}
                  </span>
                  {use.reversedAt ? (
                    <span className="text-xs text-base-content/50">
                      {t('walletReversed')}：{use.reverseReason}
                    </span>
                  ) : (
                    canAdjust &&
                    !locked && (
                      <button
                        type="button"
                        className={SMALL_BUTTON}
                        disabled={!reason.trim() || reverse.isPending}
                        onClick={() => reverse.mutate({ planId: plan.id, useId: use.id })}
                      >
                        {t('walletReverse')}
                      </button>
                    )
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}

      {/* The reason serves a manager's void and an adjuster's reversal (separate permissions). */}
      {!locked &&
        ((canManage && shown.some((p) => !p.voidedAt)) ||
          (canAdjust && shown.some((p) => p.uses.some((u) => !u.reversedAt)))) && (
          <Field label={t('walletReason')}>
            <input
              className={INPUT}
              maxLength={255}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </Field>
        )}
      {canAdjust && !locked && !integratedSales && (
        <>
          {active && (
            <form
              className="grid gap-3 sm:grid-cols-3 items-end"
              onSubmit={(e) => {
                e.preventDefault();
                record.mutate(active.id);
              }}
            >
              <Field label={t('walletTournament')}>
                <TournamentSelect value={tournamentId} onChange={setTournamentId} />
              </Field>
              <Field label={t('ledgerNote')}>
                <input
                  className={INPUT}
                  maxLength={255}
                  value={useNote}
                  onChange={(e) => setUseNote(e.target.value)}
                />
              </Field>
              <button
                type="submit"
                className={BUTTON}
                disabled={!tournamentId || !useNote.trim() || record.isPending}
              >
                {t('walletPlanRecord')}
              </button>
            </form>
          )}
          <form
            className="grid gap-3 sm:grid-cols-4 items-end"
            onSubmit={(e) => {
              e.preventDefault();
              sell.mutate();
            }}
          >
            <Field label={t('walletPlanMonth')}>
              <select className={INPUT} value={month} onChange={(e) => setMonth(e.target.value)}>
                <option value={current}>{current}</option>
                <option value={next}>{next}</option>
              </select>
            </Field>
            <Field label={t('walletPayment')}>
              <select
                className={INPUT}
                value={payment}
                onChange={(e) => setPayment(e.target.value as 'CASH' | 'ZIPA')}
              >
                <option value="CASH">{t('walletPayment_CASH')}</option>
                <option value="ZIPA">{t('walletPayment_ZIPA')}</option>
              </select>
            </Field>
            <Field label={t('ledgerNote')}>
              <input
                className={INPUT}
                maxLength={255}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </Field>
            <button type="submit" className={BUTTON} disabled={!note.trim() || sell.isPending}>
              {t('walletPlanBuy')}
            </button>
          </form>
        </>
      )}
      <FormError message={error} />
    </section>
  );
}
